'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Image } from '@/app_components/common/Image';
import { Link } from '@/app_components/common/Link';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { CheckboxV2 } from '@/app_components/ui/CheckboxV2';
import { Form, FormField } from '@/app_components/ui/form';
import { InputV2 } from '@/app_components/ui/InputV2';
import { cn } from '@/app_lib/utils/utils';

const InterestedInTypes = z.enum([
  'ceoInitiative',
  'fortuneBrainstorm',
  'fortuneGlobalForum',
  'fortuneSponsorship',
  'mostPowerfulWomen',
]);

type InterestedInTypes = z.infer<typeof InterestedInTypes>;

const leadGenFormSchema = z.object({
  company: z.string().min(1, 'Company is required'),
  consent: z.boolean().refine((value) => value, {
    message: 'Consent to contact is required',
  }),
  email: z.email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  interestedIn: z
    .record(InterestedInTypes, z.boolean().optional())
    .refine((data) => Object.values(data).some(Boolean), {
      message: 'At least one interest is required',
    }),
  lastName: z.string().min(1, 'Last name is required'),
  title: z.string().min(1, 'Job title is required'),
});
type LeadGenFormData = z.infer<typeof leadGenFormSchema>;

const interests: { image: string; label: string; value: InterestedInTypes }[] = [
  {
    image: 'https://content.fortuneconferences.com/wp-content/uploads/2026/03/fortune-ceo-initiative-square.png',
    label: 'Fortune CEO Initiative',
    value: 'ceoInitiative',
  },
  {
    image: 'https://content.fortuneconferences.com/wp-content/uploads/2026/03/Fortune-MPW.png',
    label: 'Most Powerful Women',
    value: 'mostPowerfulWomen',
  },
  {
    image: 'https://content.fortuneconferences.com/wp-content/uploads/2026/03/fortune-brainstorm-square.png',
    label: 'Fortune Brainstorm',
    value: 'fortuneBrainstorm',
  },
  {
    image: 'https://content.fortuneconferences.com/wp-content/uploads/2026/03/Fortune-Global-Forum.png',
    label: 'Fortune Global Forum',
    value: 'fortuneGlobalForum',
  },
  {
    image: 'https://content.fortuneconferences.com/wp-content/uploads/2026/03/fortune-corporate-square.png',
    label: 'Fortune Sponsorship',
    value: 'fortuneSponsorship',
  },
];

export const LeadGenForm = () => {
  const form = useForm<LeadGenFormData>({
    defaultValues: {
      company: '',
      consent: false,
      email: '',
      firstName: '',
      interestedIn: {
        ceoInitiative: false,
        fortuneBrainstorm: false,
        fortuneGlobalForum: false,
        fortuneSponsorship: false,
        mostPowerfulWomen: false,
      },
      lastName: '',
      title: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(leadGenFormSchema),
    reValidateMode: 'onBlur',
  });

  const hasSubmitted = useRef(false);

  const errors = form.formState.errors;

  const { consent } = form.watch();

  // Disabled if consent is not checked
  const isDisabled = !consent;

  const onSubmit = (_data: LeadGenFormData) => {
    //Currently not implemented yet, waiting on salesforce team
  };

  useEffect(() => {
    if (form.formState.isSubmitted && !hasSubmitted.current) {
      hasSubmitted.current = true;
    }
  }, [form.formState.isSubmitted]);

  return (
    <div>
      <Form {...form}>
        <form
          className='flex flex-col gap-lg w-full'
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className='flex flex-col md:flex-row gap-lg w-full justify-between'>
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <InputV2
                  {...field}
                  className='w-full'
                  dataCy='lead-gen-first-name'
                  error={errors.firstName?.message}
                  id='firstName'
                  label='First name'
                  placeholder='Your first name'
                  required
                />
              )}
            />
            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <InputV2
                  {...field}
                  className='w-full'
                  dataCy='lead-gen-last-name'
                  error={errors.lastName?.message}
                  id='lastName'
                  label='Last name'
                  placeholder='Your last name'
                  required
                />
              )}
            />
          </div>

          <div className='flex flex-col md:flex-row gap-lg w-full justify-between'>
            <FormField
              control={form.control}
              name='company'
              render={({ field }) => (
                <InputV2
                  {...field}
                  className='w-full'
                  dataCy='lead-gen-company'
                  error={errors.company?.message}
                  id='company'
                  label='Company name'
                  placeholder='Your company name'
                  required
                />
              )}
            />
            <FormField
              control={form.control}
              name='title'
              render={({ field }) => (
                <InputV2
                  {...field}
                  className='w-full'
                  dataCy='lead-gen-title'
                  error={errors.title?.message}
                  id='title'
                  label='Title'
                  placeholder='Your title'
                  required
                />
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <InputV2
                {...field}
                dataCy='lead-gen-email'
                error={errors.email?.message}
                id='email'
                label='E-mail'
                placeholder='Your e-mail'
                required
                type='email'
              />
            )}
          />

          <div className='flex flex-col'>
            <div className='flex items-center gap-xs'>
              <span className={cn('typography-sm', errors.interestedIn && 'text-red')}>I'm interested in:</span>
              <span
                aria-hidden='true'
                className='icon-red'
              >
                *
              </span>
            </div>
            <div className='flex flex-col gap-xs'>
              <div className='flex flex-col lg:flex-row gap-lg'>
                {interests.map((interest) => (
                  <FormField
                    control={form.control}
                    key={interest.value}
                    name={`interestedIn.${interest.value}`}
                    render={({ field }) => (
                      <label
                        className={cn(
                          'w-full lg:hover:bg-secondary lg:hover:stroke-helper cursor-pointer [&_label]:w-full items-center',
                          'py-xxs gap-lg lg:px-xxs flex flex-row-reverse lg:flex-col lg:gap-sm border-b max-lg:last:border-b-0',
                          'lg:border stroke-secondary lg:rounded-md lg:flex-1 lg:min-w-0',
                          field.value && 'border-blue-1!',
                        )}
                        htmlFor={interest.value}
                        key={interest.value}
                      >
                        <Image
                          alt={interest.label}
                          className='size-12 lg:w-full lg:h-[73px] xl:h-[90px] object-cover rounded-sm'
                          height={100}
                          src={interest.image}
                          width={120}
                        />
                        <CheckboxV2
                          checked={field.value}
                          id={interest.value}
                          label={
                            <span className='typography-sm min-w-0 w-full break-words hyphens-auto'>
                              {interest.label}
                            </span>
                          }
                          onChange={field.onChange}
                          variant='conferences'
                        />
                      </label>
                    )}
                  />
                ))}
              </div>
              {errors.interestedIn && <span className='text-red typography-xs'>{errors.interestedIn.message}</span>}
            </div>
          </div>
          <div className='pt-xs border-t stroke-secondary'>
            <FormField
              control={form.control}
              name='consent'
              render={({ field }) => (
                <CheckboxV2
                  checked={field.value}
                  {...field}
                  id='consent'
                  label={
                    <div className='typography-xs'>
                      I agree to the Fortune{' '}
                      <Link
                        className='text-blue-1 underline'
                        href='/terms-of-use/'
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        Terms of Use
                      </Link>
                      . I acknowledge that I have read the{' '}
                      <Link
                        className='text-blue-1 underline'
                        href='/privacy-policy/'
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        rel='noopener noreferrer'
                        target='_blank'
                      >
                        Privacy Policy
                      </Link>
                      . You may unsubscribe from email communication at any time.
                      <span className='text-error select-none'>*</span>
                    </div>
                  }
                  // required
                  variant='conferences'
                />
              )}
            />
          </div>
          <ButtonV2
            aria-disabled={form.formState.isSubmitting || isDisabled}
            as='button'
            disabled={form.formState.isSubmitting || isDisabled}
            noCapitalize
            // only track once submission is allowed
            optimizelyTrackingData={
              form.formState.isSubmitted && !hasSubmitted.current ? { name: 'submitted_lead_gen_form' } : undefined
            }
            size='lg'
            type='submit'
            variant='specialBlue'
          >
            <span>Keep me in the loop</span>
            {form.formState.isSubmitting && <SpinningLoader size={16} />}
          </ButtonV2>
        </form>
      </Form>
    </div>
  );
};
